import type { ComponentChildren } from "preact";
import { augmentDef, augmentGain, powerOf } from "../augments.js";
import { aimManual, build, net } from "../store.js";
import { Tooltip } from "./Tooltip.js";

const pct = (m: number): string => `${Math.round((m - 1) * 100)}%`;
const signed = (s: string): string => (s.startsWith("-") ? s : `+${s}`);

/** Plain-language explanation of each core stat shown in the HUD block. */
const STAT_HELP: Record<string, string> = {
  Move: "Movement speed multiplier.",
  Damage: "Multiplier applied to all weapon damage.",
  Cooldown: "Weapon cooldown multiplier — lower fires faster.",
  Magnet: "XP-gem pickup radius multiplier.",
  Crit: "Chance for a hit to critically strike.",
  "Crit×": "Damage multiplier on a critical strike.",
};

/**
 * Persistent bottom player HUD during a run (LoL-Swarm layout, B3): a row of 6
 * passive-augment slots (icon + stack count) and a row of 5 weapon slots (icon +
 * level), each showing filled + empty cells so the build budget is legible, plus
 * HP/level/XP, the equipped relics, and the core stat block. Slots/relics/stats all
 * have hover tooltips explaining what they do and how much they contribute.
 */
export function PlayerHud() {
  const n = net.value;
  if (n === null || n.runPhase === "lobby") return null;
  const hpFrac = n.maxHp > 0 ? Math.max(0, Math.min(1, n.hp / n.maxHp)) : 0;
  const xpFrac = n.xpToNext > 0 ? Math.max(0, Math.min(1, n.xp / n.xpToNext)) : 0;
  const b = build.value;
  const stats = b?.stats;
  const weaponSlots = b?.weaponSlots ?? 5;
  const passiveSlots = b?.passiveSlots ?? 6;
  const weapons = b?.equipped ?? [];
  const passives = b?.passives ?? [];
  const artifacts = b?.artifacts ?? [];

  return (
    <div class="absolute inset-x-0 bottom-0 flex justify-center pb-3">
      <div class="flex items-stretch gap-4 rounded-xl border border-edge bg-panel/85 px-4 py-2.5 backdrop-blur-sm">
        {/* HP + level/XP + relics */}
        <div class="flex w-44 flex-col justify-center gap-1.5">
          <div class="flex items-center justify-between text-xs">
            <span class="font-bold">{n.downed ? "DOWNED" : "HP"}</span>
            <span class="tabular-nums opacity-80">
              {Math.round(n.hp)}/{Math.round(n.maxHp)}
            </span>
          </div>
          <div class="h-2.5 overflow-hidden rounded-sm border border-edge bg-[#161b2a]">
            <div
              class="h-full transition-[width] duration-150"
              style={{
                width: `${hpFrac * 100}%`,
                background: `hsl(${Math.round(hpFrac * 120)},70%,55%)`,
              }}
            />
          </div>
          <div class="flex items-center justify-between text-xs">
            <span class="font-bold">Lv {n.level}</span>
            <span class="tabular-nums opacity-80">{n.localKills} kills</span>
          </div>
          <div class="h-2 overflow-hidden rounded-sm border border-edge bg-[#161b2a]">
            <div
              class="h-full bg-sky-300 transition-[width] duration-200"
              style={{ width: `${xpFrac * 100}%` }}
            />
          </div>
          {/* Equipped relics */}
          <div class="flex items-center gap-1.5">
            <span class="text-[10px] font-bold uppercase tracking-widest opacity-50">Relics</span>
            {artifacts.length === 0 ? (
              <span class="text-[10px] opacity-40">none</span>
            ) : (
              <div class="flex gap-1">
                {artifacts.map((art) => (
                  <Tooltip
                    key={art.id}
                    content={
                      <>
                        <div class="font-bold">
                          {art.icon} {art.name}
                        </div>
                        <div class="mt-0.5 opacity-80">{art.desc}</div>
                      </>
                    }
                  >
                    <span class="flex h-6 w-6 items-center justify-center rounded-md border border-amber-400/40 bg-amber-400/10 text-sm">
                      {art.icon}
                    </span>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
          <div class="flex items-center justify-between text-[10px] uppercase tracking-wider opacity-70">
            <span>Aim</span>
            <span class={aimManual.value ? "font-bold text-amber-300" : "opacity-70"}>
              {aimManual.value ? "Manual" : "Auto"} <span class="opacity-50">(Space)</span>
            </span>
          </div>
        </div>

        {/* Slot rows: passive augments (6) over weapons (5) */}
        <div class="flex flex-col justify-center gap-1.5 border-l border-edge pl-4">
          <div class="flex items-center gap-2">
            <span class="w-12 text-[10px] font-bold uppercase tracking-widest opacity-50">
              Augm.
            </span>
            <div class="flex gap-1">
              {Array.from({ length: passiveSlots }, (_, i) => {
                const p = passives[i];
                if (p === undefined) {
                  return (
                    <Slot
                      key={i}
                      title="Empty augment slot"
                      label=""
                      badge=""
                      maxed={false}
                      accent="sky"
                    />
                  );
                }
                const def = augmentDef(p.id);
                const gain = def !== undefined ? augmentGain(def, p.count) : null;
                return (
                  <Slot
                    key={i}
                    label={abbr(p.name)}
                    badge={`${p.count}`}
                    maxed={p.count >= p.maxStacks}
                    accent="sky"
                    tip={
                      <>
                        <div class="font-bold text-sky-200">{p.name}</div>
                        {def?.desc !== undefined && <div class="mt-0.5 opacity-80">{def.desc}</div>}
                        {gain !== null && (
                          <div class="mt-1 font-semibold text-emerald-300">Current: {gain}</div>
                        )}
                        <div class="mt-0.5 opacity-55">
                          Stacks {p.count}/{p.maxStacks}
                        </div>
                      </>
                    }
                  />
                );
              })}
            </div>
          </div>
          <div class="flex items-center gap-2">
            <span class="w-12 text-[10px] font-bold uppercase tracking-widest opacity-50">
              Wpns
            </span>
            <div class="flex gap-1">
              {Array.from({ length: weaponSlots }, (_, i) => {
                const wpn = weapons[i];
                if (wpn === undefined) {
                  return (
                    <Slot
                      key={i}
                      title="Empty weapon slot"
                      label=""
                      badge=""
                      maxed={false}
                      accent="emerald"
                    />
                  );
                }
                const power = powerOf(wpn.power);
                const dtype = power?.damageType;
                return (
                  <Slot
                    key={i}
                    label={abbr(wpn.name)}
                    badge={`L${wpn.level}`}
                    maxed={wpn.level >= wpn.maxLevel}
                    accent="emerald"
                    tip={
                      <>
                        <div class="font-bold text-emerald-200">{wpn.name}</div>
                        {dtype !== undefined && (
                          <div
                            class="mt-0.5 font-semibold"
                            style={{ color: dtype === "magic" ? "#9a6bff" : "#ffb24a" }}
                          >
                            {dtype === "magic" ? "✦ Magic" : "⚔️ Physical"} damage
                          </div>
                        )}
                        <div class="mt-1 opacity-80">
                          Level {wpn.level}/{wpn.maxLevel} — each level adds damage
                          {power !== undefined ? " / projectiles" : ""}.
                        </div>
                      </>
                    }
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Stats block */}
        {stats !== undefined && (
          <div class="grid grid-cols-2 gap-x-3 gap-y-0.5 border-l border-edge pl-4 text-xs tabular-nums">
            <Stat label="Move" v={signed(pct(stats.moveMul))} />
            <Stat label="Damage" v={signed(pct(stats.damageMul))} />
            <Stat label="Cooldown" v={signed(pct(stats.cooldownMul))} />
            <Stat label="Magnet" v={signed(pct(stats.magnetMul))} />
            <Stat label="Crit" v={`${Math.round(stats.critChance * 100)}%`} />
            <Stat label="Crit×" v={`${stats.critMul.toFixed(1)}`} />
          </div>
        )}
      </div>
    </div>
  );
}

/** A 3-letter abbreviation for a power/augment name (slots are tiny). */
function abbr(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase();
  }
  return name.slice(0, 3).toUpperCase();
}

const ACCENTS: Record<string, { border: string; bg: string; max: string }> = {
  emerald: {
    border: "border-emerald-400/40",
    bg: "bg-emerald-400/10",
    max: "border-emerald-300 bg-emerald-400/25 shadow-[0_0_6px] shadow-emerald-400/40",
  },
  sky: {
    border: "border-sky-400/35",
    bg: "bg-sky-400/10",
    max: "border-sky-300 bg-sky-400/25 shadow-[0_0_6px] shadow-sky-400/40",
  },
};

/** One build slot cell: filled (with a label + count/level badge) or a dim empty placeholder. */
function Slot({
  title,
  label,
  badge,
  maxed,
  accent,
  tip,
}: {
  title?: string;
  label: string;
  badge: string;
  maxed: boolean;
  accent: string;
  tip?: ComponentChildren;
}) {
  const a = ACCENTS[accent] ?? ACCENTS.emerald;
  const filled = label !== "";
  const cls = !filled
    ? "border-dashed border-edge/60 bg-[#161b2a]/40 opacity-50"
    : maxed
      ? a?.max
      : `${a?.border} ${a?.bg}`;
  const cell = (
    <div
      title={tip === undefined ? title : undefined}
      class={`relative flex h-9 w-9 items-center justify-center rounded-md border text-[11px] font-bold tabular-nums ${cls}`}
    >
      <span>{label}</span>
      {badge !== "" && (
        <span class="absolute -bottom-1 -right-1 rounded-sm bg-[#0b0e16] px-1 text-[9px] leading-tight opacity-90">
          {badge}
        </span>
      )}
    </div>
  );
  return tip === undefined ? cell : <Tooltip content={tip}>{cell}</Tooltip>;
}

function Stat({ label, v }: { label: string; v: string }) {
  const help = STAT_HELP[label];
  const row = (
    <div class="flex w-full justify-between gap-2">
      <span class="opacity-55">{label}</span>
      <span class="font-semibold">{v}</span>
    </div>
  );
  if (help === undefined) return row;
  return (
    <Tooltip
      width={180}
      content={
        <>
          <div class="font-bold">{label}</div>
          <div class="mt-0.5 opacity-80">{help}</div>
          <div class="mt-1 font-semibold text-emerald-300">Current: {v}</div>
        </>
      }
    >
      {row}
    </Tooltip>
  );
}
